import {Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faSpinnerThird, faTriangleExclamation} from '@fortawesome/pro-solid-svg-icons';
import {
  BicButtonComponent,
  BicPopupComponent,
  BicToastService,
  TooltipDirective,
} from '@buildinginformationcloud/bic-ui-components';
import {Observable} from 'rxjs';
import {ProjectShareTarget, ReferencedRuleset, WorkflowService} from '../../../../services/workflow.service';
import {RulesetService} from '../../../../services/ruleset.service';
import {Ruleset} from '../../../ruleManagement/models/rule-management.models';
import {ProjectShareEntity} from '../share-with-projects-prompt/share-with-projects-prompt.component';
import {serverErrorMessage} from '../server-error-message';

/**
 * `delete`: Löschen eines geteilten Eintrags – Besitz übergeben oder aus allen Projekten löschen.
 * `transfer`: nur den Eigentümer wechseln.
 */
export type ProjectOwnershipMode = 'delete' | 'transfer';

type DeleteChoice = 'transfer' | 'delete-everywhere';

/**
 * Eigentümer eines zwischen Projekten geteilten Prüflaufs oder myChecks (Prüfregelset) wechseln bzw.
 * ihn löschen.
 *
 * <p>Zur Wahl als neuer Eigentümer stehen nur Projekte, mit denen schon geteilt ist und für die die
 * Teilen-Regel erfüllt ist ({@link ProjectShareTarget#allowed}). „Aus allen Projekten löschen" geht
 * nur, wenn sie für <b>jedes</b> verlinkte Projekt erfüllt ist – das Backend prüft es ohnehin, der
 * Dialog sperrt die Option nur vorab mit Begründung.
 */
@Component({
  selector: 'app-project-ownership-prompt',
  standalone: true,
  imports: [FormsModule, FaIconComponent, BicPopupComponent, BicButtonComponent, TooltipDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './project-ownership-prompt.component.html',
})
export class ProjectOwnershipPromptComponent implements OnInit {
  @Input() entity: ProjectShareEntity = 'ruleset';
  /** ID des Prüflaufs bzw. myChecks. */
  @Input({required: true}) itemId!: string;
  @Input({required: true}) itemName!: string;
  /** Nur bei `entity = 'ruleset'`: zum Löschen nötig, weil jede Art einen eigenen Löschweg hat. */
  @Input() ruleset?: Ruleset;
  /** Aktuelles Eigentümerprojekt. */
  @Input({required: true}) projectId!: number;
  @Input() mode: ProjectOwnershipMode = 'delete';

  @Output() closed = new EventEmitter<void>();
  /** `deleted`: der Eintrag ist hier weg; `transferred`: er gehört jetzt einem anderen Projekt. */
  @Output() done = new EventEmitter<'deleted' | 'transferred'>();

  private readonly rulesetService = inject(RulesetService);
  private readonly flowService = inject(WorkflowService);
  private readonly toastService = inject(BicToastService);

  protected readonly faSpinnerThird = faSpinnerThird;
  protected readonly faTriangleExclamation = faTriangleExclamation;
  /** Siehe share-with-projects-prompt: `undefined` statt `''`, sonst leerer Tooltip-Pfeil. */
  protected readonly noTooltip = undefined as unknown as string;

  protected loading = true;
  protected loadFailed = false;
  protected saving = false;
  /** Alle Projekte, mit denen geteilt ist. */
  protected linked: ProjectShareTarget[] = [];

  protected choice: DeleteChoice = 'transfer';
  protected newOwnerProjectId: number | null = null;
  /** Nur im Modus `transfer`: das bisherige Eigentümerprojekt behält den Eintrag als geteilten. */
  protected keepCurrentOwner = true;

  /**
   * Nur bei Prüfläufen: die verwendeten myChecks samt Freigabe-Stand. Sie wandern beim
   * Eigentümerwechsel nicht mit – `null`, solange nicht (erfolgreich) geladen.
   */
  protected referencedRulesets: ReferencedRuleset[] | null = null;
  protected referencedRulesetsLoadFailed = false;

  protected get isRuleset(): boolean {
    return this.entity === 'ruleset';
  }

  /** „der myCheck" / „der Prüflauf" – für Texte mitten im Satz (Nominativ). */
  protected get noun(): string {
    return this.isRuleset ? 'der myCheck' : 'der Prüflauf';
  }

  /** Warnung zu den myChecks nur, wo sie zählt: Prüflauf wird ohne Behalten übergeben. */
  protected get showRulesetWarningSection(): boolean {
    return !this.isRuleset && this.isTransfer && this.currentOwnerLeaves && this.newOwnerProjectId != null;
  }

  /**
   * myChecks des Prüflaufs, die im alten Projekt bleiben und mit dem neuen Eigentümer nicht geteilt
   * sind: dort fehlen sie in den Listen. Die Ausführung läuft weiter (Regelsets werden per ID
   * geladen), bearbeiten oder neu zuordnen kann man sie dort aber nicht.
   */
  protected get rulesetsMissingAtNewOwner(): ReferencedRuleset[] {
    const newOwner = this.newOwnerProjectId;
    if (newOwner == null || !this.referencedRulesets) return [];
    return this.referencedRulesets.filter(r =>
      r.ownerProjectId !== newOwner && !(r.linkedProjectIds ?? []).includes(newOwner));
  }

  protected get newOwnerName(): string {
    return this.linked.find(t => t.projectId === this.newOwnerProjectId)?.name ?? 'dem neuen Eigentümer';
  }

  /** Das bisherige Projekt behält den Eintrag nicht – beim Löschen nie, sonst je nach Häkchen. */
  private get currentOwnerLeaves(): boolean {
    return this.isDeleteMode || !this.keepCurrentOwner;
  }

  ngOnInit(): void {
    const targets$: Observable<ProjectShareTarget[]> = this.isRuleset
      ? this.rulesetService.listProjectShareTargets(this.itemId, this.projectId)
      : this.flowService.listProjectShareTargets(this.itemId, this.projectId);
    targets$.subscribe({
      next: targets => {
        this.linked = targets.filter(t => t.linked);
        this.newOwnerProjectId = this.transferCandidates[0]?.projectId ?? null;
        if (this.isDeleteMode && this.transferCandidates.length === 0 && this.canDeleteEverywhere) {
          this.choice = 'delete-everywhere';
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.loadFailed = true;
      },
    });

    if (!this.isRuleset) {
      this.flowService.listReferencedRulesetsForShare(this.itemId, this.projectId).subscribe({
        next: rulesets => this.referencedRulesets = rulesets ?? [],
        error: err => {
          // Nicht blockierend – die Übergabe selbst geht auch ohne –, aber sichtbar: sonst fehlte
          // die Warnung vor myChecks, die beim neuen Eigentümer nicht in den Listen stehen.
          console.error('[ProjectOwnershipPrompt] Verwendete myChecks nicht lesbar', err);
          this.referencedRulesetsLoadFailed = true;
          this.toastService.error(serverErrorMessage(err,
            'Die verwendeten myChecks des Prüflaufs konnten nicht geladen werden.'));
        },
      });
    }
  }

  protected get isDeleteMode(): boolean {
    return this.mode === 'delete';
  }

  /** Mögliche neue Eigentümer: geteilt und Teilen-Regel erfüllt. */
  protected get transferCandidates(): ProjectShareTarget[] {
    return this.linked.filter(t => t.allowed);
  }

  /** Überall löschen verlangt „myCheck löschen" statt „verwalten" – bei Custom Roles ein Unterschied. */
  protected get canDeleteEverywhere(): boolean {
    return this.linked.length > 0 && this.linked.every(t => t.deleteAllowed);
  }

  /** Projekte, für die die Rechte fehlen – nennt der Tooltip von „Aus allen Projekten löschen". */
  protected get deleteEverywhereTooltip(): string {
    if (this.canDeleteEverywhere) return this.noTooltip;
    const missing = this.linked.filter(t => !t.deleteAllowed).map(t => t.name).join(', ');
    return `Dafür fehlen Ihnen die Rechte in: ${missing}.`;
  }

  protected get transferTooltip(): string {
    return this.transferCandidates.length > 0
      ? this.noTooltip
      : `Für keines der Projekte, mit denen ${this.noun} geteilt ist, haben Sie die nötigen Rechte.`;
  }

  protected get isTransfer(): boolean {
    return !this.isDeleteMode || this.choice === 'transfer';
  }

  protected get confirmDisabled(): boolean {
    if (this.loading || this.loadFailed || this.saving) return true;
    if (this.isTransfer) return this.newOwnerProjectId == null;
    return !this.canDeleteEverywhere;
  }

  protected get confirmLabel(): string {
    if (!this.isDeleteMode) return 'Eigentümer ändern';
    if (this.choice === 'transfer') return 'Übergeben und entfernen';
    return this.isRuleset ? 'Aus allen Projekten löschen' : 'Überall entfernen';
  }

  protected choose(choice: DeleteChoice): void {
    if (this.saving) return;
    if (choice === 'transfer' && this.transferCandidates.length === 0) return;
    if (choice === 'delete-everywhere' && !this.canDeleteEverywhere) return;
    this.choice = choice;
  }

  protected selectOwner(target: ProjectShareTarget): void {
    if (!target.allowed || this.saving) return;
    this.newOwnerProjectId = target.projectId;
  }

  protected onClose(): void {
    if (this.saving) return;
    this.closed.emit();
  }

  protected onConfirm(): void {
    if (this.confirmDisabled) return;
    this.saving = true;

    if (this.isTransfer) {
      // Beim Löschen behält das bisherige Projekt den Eintrag nie – das ist ja der Sinn des Löschens.
      const keep = this.isDeleteMode ? false : this.keepCurrentOwner;
      const newOwner = this.linked.find(t => t.projectId === this.newOwnerProjectId);
      const transfer$ = this.isRuleset
        ? this.rulesetService.transferOwnership(this.itemId, this.projectId, this.newOwnerProjectId!, keep)
        : this.flowService.transferOwnership(this.itemId, this.projectId, this.newOwnerProjectId!, keep);
      transfer$.subscribe({
        next: () => {
          this.saving = false;
          this.toastService.success(keep
            ? `„${newOwner?.name}" ist jetzt Eigentümer.`
            : `„${newOwner?.name}" ist jetzt Eigentümer, ${this.noun} wurde aus diesem Projekt entfernt.`);
          this.done.emit(keep ? 'transferred' : 'deleted');
        },
        error: err => this.fail(err, 'Der Eigentümer konnte nicht geändert werden.'),
      });
      return;
    }

    const delete$: Observable<unknown> = this.isRuleset
      ? this.rulesetService.deleteRuleset(this.ruleset!, this.projectId, true)
      : this.flowService.archiveDefinitionEverywhere(this.itemId, this.projectId);
    delete$.subscribe({
      next: () => {
        this.saving = false;
        this.toastService.success(this.isRuleset
          ? 'myCheck aus allen Projekten gelöscht.'
          : 'Prüflauf aus allen Projekten entfernt und archiviert.');
        this.done.emit('deleted');
      },
      error: err => this.fail(err, this.isRuleset ? 'myCheck konnte nicht gelöscht werden.' : 'Prüflauf konnte nicht archiviert werden.'),
    });
  }

  private fail(err: unknown, fallback: string): void {
    this.saving = false;
    this.toastService.error(serverErrorMessage(err, fallback));
  }
}
